import { supabase } from './supabaseClient';

/**
 * Scoring Engine Service
 *
 * Dynamic scoring system that evaluates user profiles against configurable rules
 * per country and immigration pathway. The engine is completely data-driven through
 * the Supabase database tables.
 */
export class ScoringEngine {

  /**
   * Calculate scores for all available pathways for a given country
   * @param {Object} userProfile - User profile data
   * @param {Object} cvAnalysis - Optional CV analysis data
   * @param {string} countryCode - ISO country code (e.g., 'DEU', 'CAN')
   * @returns {Object} - Scoring results for all pathways
   */
  static async calculateAllPathwayScores(userProfile, cvAnalysis = null, countryCode = 'DEU') {
    try {
      // Get country information
      const { data: country, error: countryError } = await supabase
        .from('countries')
        .select('*')
        .eq('code', countryCode)
        .single();

      if (countryError || !country) {
        throw new Error(`Country not found: ${countryCode}`);
      }

      // Get all active pathways for the country
      const { data: pathways, error: pathwaysError } = await supabase
        .from('immigration_pathways')
        .select(`
          *,
          scoring_criteria(
            *,
            scoring_rules(*)
          )
        `)
        .eq('country_id', country.id)
        .eq('is_active', true)
        .order('sort_order');

      if (pathwaysError) {
        throw new Error(`Failed to load pathways: ${pathwaysError.message}`);
      }

      // Calculate scores for each pathway
      const results = {};
      for (const pathway of pathways) {
        results[pathway.pathway_key] = await this.calculatePathwayScore(
          userProfile,
          cvAnalysis,
          pathway
        );
      }

      return {
        country: country,
        userProfile: userProfile,
        timestamp: new Date().toISOString(),
        pathways: results,
        bestMatch: this.findBestPathway(results)
      };

    } catch (error) {
      console.error('Error calculating pathway scores:', error);
      throw error;
    }
  }

  /**
   * Calculate score for a specific pathway
   * @param {Object} userProfile - User profile data
   * @param {Object} cvAnalysis - Optional CV analysis data
   * @param {Object} pathway - Pathway object with scoring criteria
   * @returns {Object} - Detailed scoring result
   */
  static async calculatePathwayScore(userProfile, cvAnalysis, pathway) {
    try {
      let totalScore = 0;
      let maxPossibleScore = 0;
      const breakdown = {};
      const matchedRules = [];

      // Process each scoring criterion
      for (const criterion of pathway.scoring_criteria || []) {
        if (!criterion.is_active) continue;

        const criterionResult = this.evaluateCriterion(
          userProfile,
          cvAnalysis,
          criterion
        );

        totalScore += criterionResult.score;
        maxPossibleScore += criterionResult.maxContribution;
        breakdown[criterion.criteria_key] = criterionResult;

        // Store matched rules for reporting
        if (criterionResult.matchedRules.length > 0) {
          matchedRules.push(...criterionResult.matchedRules);
        }
      }

      // Calculate percentage score
      const percentageScore = maxPossibleScore > 0
        ? Math.round((totalScore / maxPossibleScore) * 100)
        : 0;

      // Generate recommendations based on score
      const recommendations = this.generateRecommendations(
        pathway,
        totalScore,
        maxPossibleScore,
        breakdown
      );

      return {
        pathwayKey: pathway.pathway_key,
        pathwayName: pathway.pathway_key.replace(/_/g, ' ').toUpperCase(),
        baseCost: pathway.base_cost,
        processingDays: pathway.processing_days,
        successRate: pathway.success_rate,
        totalScore,
        maxPossibleScore,
        percentageScore,
        breakdown,
        matchedRules,
        recommendations,
        isEligible: percentageScore >= 60, // 60% threshold
        confidence: this.calculateConfidence(breakdown, cvAnalysis)
      };

    } catch (error) {
      console.error(`Error calculating score for pathway ${pathway.pathway_key}:`, error);
      throw error;
    }
  }

  /**
   * Evaluate a specific scoring criterion
   * @param {Object} userProfile - User profile data
   * @param {Object} cvAnalysis - CV analysis data
   * @param {Object} criterion - Scoring criterion with rules
   * @returns {Object} - Criterion evaluation result
   */
  static evaluateCriterion(userProfile, cvAnalysis, criterion) {
    let result;

    // Evaluate based on criterion type
    switch (criterion.criteria_key) {
      case 'education':
        result = this.evaluateEducation(
          userProfile, cvAnalysis, criterion.scoring_rules
        );
        break;

      case 'language':
        result = this.evaluateLanguage(
          userProfile, cvAnalysis, criterion.scoring_rules
        );
        break;

      case 'experience':
        result = this.evaluateExperience(
          userProfile, cvAnalysis, criterion.scoring_rules
        );
        break;

      case 'age':
        result = this.evaluateAge(
          userProfile, cvAnalysis, criterion.scoring_rules
        );
        break;

      case 'financial':
        result = this.evaluateFinancial(
          userProfile, cvAnalysis, criterion.scoring_rules
        );
        break;

      default:
        // Generic evaluation for custom criteria
        result = this.evaluateGeneric(
          userProfile, cvAnalysis, criterion.scoring_rules
        );
    }

    if (!result || typeof result !== 'object') {
      console.error('Invalid result from evaluation:', result);
      return {
        criterionKey: criterion.criteria_key,
        maxPoints: criterion.max_points,
        weight: criterion.weight,
        score: 0,
        rawScore: 0,
        matchedRules: [],
        details: {},
        percentage: 0,
        maxContribution: 0
      };
    }

    const {
      score: criterionScore,
      matchedRules,
      details,
      maxPoints: criterionMaxPoints
    } = result;

    const rawMaxPoints = Number.isFinite(criterionMaxPoints)
      ? criterionMaxPoints
      : Number.isFinite(criterion.max_points)
        ? criterion.max_points
        : 0;

    const targetMaxPoints = Number.isFinite(criterion.max_points)
      ? criterion.max_points
      : rawMaxPoints;

    const safeRawMax = rawMaxPoints > 0 ? rawMaxPoints : (targetMaxPoints || 0);
    const clampedScore = safeRawMax > 0
      ? Math.min(Math.max(criterionScore, 0), safeRawMax)
      : 0;

    const rescaledScore = safeRawMax > 0
      ? (clampedScore / safeRawMax) * targetMaxPoints
      : 0;

    const weight = Number.isFinite(criterion.weight) ? criterion.weight : 1;
    const maxContribution = Math.round(targetMaxPoints * weight);
    const weightedScore = Math.round(rescaledScore * weight);

    return {
      criterionKey: criterion.criteria_key,
      maxPoints: criterion.max_points,
      weight: criterion.weight,
      score: weightedScore,
      rawScore: criterionScore,
      matchedRules,
      details,
      percentage: maxContribution > 0
        ? Math.round((weightedScore / maxContribution) * 100)
        : 0,
      maxContribution
    };
  }

  /**
   * Education evaluation logic
   */
  static evaluateEducation(userProfile, cvAnalysis, rules) {
    let score = 0;
    const matchedRules = [];
    const highestLevel = userProfile.education?.highestLevel || userProfile.education?.highestEducation || '';
    const details = { educationLevel: highestLevel || 'unknown' };

    for (const rule of rules || []) {
      if (!rule.is_active) continue;

      const educationLevel = highestLevel.toString().toLowerCase();
      const cvEducationLevel = (cvAnalysis?.educationAssessment?.level || cvAnalysis?.education?.level || '').toString().toLowerCase();
      const courseOfStudy = (userProfile.education?.courseOfStudy || '').toString().toLowerCase();

      if (this.matchesEducationRule(rule.rule_condition, educationLevel, cvEducationLevel, courseOfStudy)) {
        score += rule.points;
        matchedRules.push({
          condition: rule.rule_condition,
          points: rule.points,
          description: `Education level/course matches: ${rule.rule_condition}`
        });
      }
    }

    const maxPoints = (rules || []).reduce((sum, rule) => rule.is_active ? sum + rule.points : sum, 0);
    return { score, matchedRules, details, maxPoints };
  }

  /**
   * Language skills evaluation logic
   */
  static evaluateLanguage(userProfile, cvAnalysis, rules) {
    let score = 0;
    const matchedRules = [];
    const englishLevel = userProfile.languageSkills?.english || userProfile.languageSkills?.englishProficiency || '';
    const germanLevel = userProfile.languageSkills?.german || userProfile.languageSkills?.germanLevel || '';
    const cvLanguageLevel = cvAnalysis?.languageSkills?.level || cvAnalysis?.skillsEvaluation?.languageSkills?.english || '';
    const details = {
      englishLevel: englishLevel || 'unknown',
      germanLevel: germanLevel || 'unknown'
    };

    for (const rule of rules || []) {
      if (!rule.is_active) continue;

      if (this.matchesLanguageRule(rule.rule_condition, englishLevel, germanLevel, cvLanguageLevel)) {
        score += rule.points;
        matchedRules.push({
          condition: rule.rule_condition,
          points: rule.points,
          description: `Language proficiency matches: ${rule.rule_condition}`
        });
      }
    }

    const maxPoints = (rules || []).reduce((sum, rule) => rule.is_active ? sum + rule.points : sum, 0);
    return { score, matchedRules, details, maxPoints };
  }

  /**
   * Work experience evaluation logic
   */
  static evaluateExperience(userProfile, cvAnalysis, rules) {
    let score = 0;
    const matchedRules = [];
    const yearsOfExperience = this.deriveYearsOfExperience(userProfile, cvAnalysis);
    const details = { yearsOfExperience };

    for (const rule of rules || []) {
      if (!rule.is_active) continue;

      if (this.matchesExperienceRule(rule.rule_condition, yearsOfExperience)) {
        score += rule.points;
        matchedRules.push({
          condition: rule.rule_condition,
          points: rule.points,
          description: `Experience matches: ${rule.rule_condition}`
        });
      }
    }

    const maxPoints = (rules || []).reduce((sum, rule) => rule.is_active ? sum + rule.points : sum, 0);
    return { score, matchedRules, details, maxPoints };
  }

  /**
   * Age evaluation logic
   */
  static evaluateAge(userProfile, cvAnalysis, rules) {
    let score = 0;
    const matchedRules = [];
    const dateOfBirth = userProfile.personalInfo?.dateOfBirth;
    const details = { dateOfBirth };

    if (dateOfBirth) {
      const age = this.calculateAge(dateOfBirth);
      details.age = age;

      for (const rule of rules || []) {
        if (!rule.is_active) continue;

        if (this.matchesAgeRule(rule.rule_condition, age, rule.min_value, rule.max_value)) {
          score += rule.points;
          matchedRules.push({
            condition: rule.rule_condition,
            points: rule.points,
            description: `Age matches: ${rule.rule_condition} (Age: ${age})`
          });
        }
      }
    }

    const maxPoints = (rules || []).reduce((sum, rule) => rule.is_active ? sum + rule.points : sum, 0);
    return { score, matchedRules, details, maxPoints };
  }

  /**
   * Financial evaluation logic
   */
  static evaluateFinancial(userProfile, cvAnalysis, rules) {
    let score = 0;
    const matchedRules = [];
    const fundsAvailable = parseFloat(userProfile.financialInfo?.fundsAvailable || userProfile.financialInfo?.availableFunds || userProfile.financialInfo?.totalBudget || userProfile.financial_info?.educationBudget || 0);
    const monthlyIncome = parseFloat(userProfile.financialInfo?.monthlyIncome || userProfile.financial_info?.monthlyIncome || 0);
    const details = { fundsAvailable, monthlyIncome };

    for (const rule of rules || []) {
      if (!rule.is_active) continue;

      if (this.matchesFinancialRule(rule.rule_condition, fundsAvailable, monthlyIncome)) {
        score += rule.points;
        matchedRules.push({
          condition: rule.rule_condition,
          points: rule.points,
          description: `Financial requirements match: ${rule.rule_condition}`
        });
      }
    }

    const maxPoints = (rules || []).reduce((sum, rule) => rule.is_active ? sum + rule.points : sum, 0);
    return { score, matchedRules, details, maxPoints };
  }

  /**
   * Generic evaluation for custom criteria
   */
  static evaluateGeneric(userProfile, cvAnalysis, rules) {
    let score = 0;
    const matchedRules = [];

    for (const rule of rules || []) {
      if (!rule.is_active) continue;

      const match = this.matchesGenericRule(rule.rule_condition, userProfile, cvAnalysis);
      if (!match) continue;

      score += rule.points;
      matchedRules.push({
        condition: rule.rule_condition,
        points: rule.points,
        description: `Custom criterion matched: ${rule.rule_condition}`
      });
    }

    const maxPoints = (rules || []).reduce((sum, rule) => rule.is_active ? sum + rule.points : sum, 0);
    return { score, matchedRules, details: {}, maxPoints };
  }

  // Helper methods for rule matching
  static matchesEducationRule(condition, educationLevel, cvEducationLevel, courseOfStudy) {
    if (!condition) return false;
    const normalize = (value) => (value || '').replace(/[^a-z0-9+]/g, ' ').toLowerCase();
    const tokens = condition.split(/[,|]/).map(token => normalize(token.trim())).filter(Boolean);
    if (!tokens.length) return false;
    const educationTokens = normalize(educationLevel);
    const cvTokens = normalize(cvEducationLevel);
    const courseTokens = normalize(courseOfStudy);
    return tokens.some(token => educationTokens.includes(token) || cvTokens.includes(token) || courseTokens.includes(token));
  }

  static matchesLanguageRule(condition, englishLevel, germanLevel, cvLanguageLevel) {
    if (!condition) return false;
    const conditionLower = condition.toLowerCase();
    const comparator = (value) => (value || '').toString().toLowerCase();
    const english = comparator(englishLevel);
    const german = comparator(germanLevel);
    const cvLang = comparator(cvLanguageLevel);

    const scoreMatch = conditionLower.match(/ielts\s*([0-9.]+)/);
    if (scoreMatch) {
      const required = parseFloat(scoreMatch[1]);
      const actual = parseFloat(english.match(/([0-9.]+)/)?.[1] || cvLang.match(/([0-9.]+)/)?.[1]);
      if (Number.isFinite(required) && Number.isFinite(actual)) {
        return actual >= required;
      }
    }

    return english.includes(conditionLower) ||
      german.includes(conditionLower) ||
      cvLang.includes(conditionLower);
  }

  static matchesExperienceRule(condition, yearsOfExperience) {
    if (!condition) return false;

    const parsedRange = this.parseNumericRange(condition);
    if (parsedRange) {
      return this.isWithinRange(yearsOfExperience, parsedRange);
    }

    const numeric = this.toNumber(condition);
    return Number.isFinite(numeric) ? yearsOfExperience >= numeric : false;
  }

  static matchesAgeRule(condition, age, minValue, maxValue) {
    if (Number.isFinite(minValue) || Number.isFinite(maxValue)) {
      const lower = Number.isFinite(minValue) ? minValue : -Infinity;
      const upper = Number.isFinite(maxValue) ? maxValue : Infinity;
      if (age >= lower && age <= upper) {
        return true;
      }
    }

    const range = this.parseNumericRange(condition);
    if (range) {
      return this.isWithinRange(age, range);
    }

    return false;
  }

  static matchesFinancialRule(condition, fundsAvailable, monthlyIncome) {
    if (!condition) return false;
    const lower = condition.toLowerCase();
    const targetValue = lower.includes('income') || lower.includes('salary')
      ? monthlyIncome
      : fundsAvailable;

    const range = this.parseNumericRange(condition);
    if (range && Number.isFinite(targetValue)) {
      return this.isWithinRange(targetValue, range);
    }

    const numeric = this.toNumber(condition);
    if (Number.isFinite(numeric)) {
      return Number.isFinite(targetValue) ? targetValue >= numeric : false;
    }

    return false;
  }

  static matchesGenericRule(condition, userProfile, cvAnalysis) {
    const normalized = (condition || '').toLowerCase();
    if (!normalized) return true;

    if (normalized.includes('loan')) {
      const needsLoan = Boolean(userProfile.financialInfo?.requiresLoan || userProfile.financialInfo?.hasLoanRequirement);
      return needsLoan;
    }

    if (normalized.includes('language')) {
      const englishLevel = (userProfile.languageSkills?.english || '').toLowerCase();
      return englishLevel !== 'fluent';
    }

    if (normalized.includes('gap')) {
      const gaps = cvAnalysis?.experienceAnalysis?.employmentGaps;
      return Array.isArray(gaps) && gaps.length > 0;
    }

    return true;
  }

  static calculateAge(dateOfBirth) {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    return age;
  }

  static findBestPathway(results) {
    let bestPathway = null;
    let highestScore = 0;

    for (const [pathwayKey, result] of Object.entries(results)) {
      if (result.percentageScore > highestScore) {
        highestScore = result.percentageScore;
        bestPathway = pathwayKey;
      }
    }

    return {
      pathway: bestPathway,
      score: highestScore,
      result: bestPathway ? results[bestPathway] : null
    };
  }

  static generateRecommendations(pathway, totalScore, maxPossibleScore, breakdown) {
    const recommendations = [];
    const safeMax = maxPossibleScore > 0 ? maxPossibleScore : 1;
    const percentageScore = Math.round((totalScore / safeMax) * 100);

    if (percentageScore >= 80) {
      recommendations.push({
        type: 'success',
        title: 'Excellent Match',
        message: `You are an excellent candidate for the ${pathway.pathway_key.replace(/_/g, ' ').toUpperCase()} pathway.`,
        priority: 'high'
      });
    } else if (percentageScore >= 60) {
      recommendations.push({
        type: 'good',
        title: 'Good Match',
        message: `You have a good chance of qualifying for the ${pathway.pathway_key.replace(/_/g, ' ').toUpperCase()} pathway.`,
        priority: 'medium'
      });
    } else {
      recommendations.push({
        type: 'warning',
        title: 'Consider Improvements',
        message: `Consider improving certain areas to strengthen your application.`,
        priority: 'low'
      });
    }

    // Add specific recommendations based on breakdown
    for (const [criterion, result] of Object.entries(breakdown)) {
      if (result.percentage < 50) {
        recommendations.push({
          type: 'improvement',
          title: `Improve ${criterion}`,
          message: `Focus on strengthening your ${criterion} profile to increase your chances.`,
          priority: 'medium'
        });
      }
    }

    return recommendations;
  }

  static calculateConfidence(breakdown, cvAnalysis) {
    let confidenceScore = 0.7; // Base confidence

    // Increase confidence if CV analysis is available
    if (cvAnalysis) {
      confidenceScore += 0.2;
    }

    // Adjust based on how complete the profile is
    const criteriaCount = Object.keys(breakdown).length;
    const maxCriteria = 5; // education, language, experience, age, financial

    if (criteriaCount >= maxCriteria) {
      confidenceScore += 0.1;
    }

    return Math.min(confidenceScore, 1.0);
  }

  static deriveYearsOfExperience(userProfile, cvAnalysis) {
    const fromCv = this.extractNumericFields(
      cvAnalysis?.experienceAnalysis,
      ['totalYears', 'totalExperienceYears', 'relevantExperience', 'experienceYears']
    );
    if (Number.isFinite(fromCv) && fromCv > 0) {
      return fromCv;
    }

    const positions = cvAnalysis?.experienceAnalysis?.positions;
    if (Array.isArray(positions) && positions.length) {
      const totals = positions
        .map((position) => this.derivePositionDuration(position))
        .filter((value) => Number.isFinite(value) && value > 0);

      if (totals.length) {
        const totalYears = totals.reduce((sum, value) => sum + value, 0);
        if (totalYears > 0) {
          return parseFloat(totalYears.toFixed(2));
        }
      }
    }

    const profileTotals = [
      userProfile.experience?.totalYears,
      userProfile.experience_info?.totalExperience,
      userProfile.experience_info?.[0]?.totalExperience,
      userProfile.experience?.totalExperienceYears,
      userProfile.experience?.totalExperience
    ];

    for (const value of profileTotals) {
      const numeric = this.toNumber(value);
      if (Number.isFinite(numeric) && numeric > 0) {
        return numeric;
      }
    }

    if (Array.isArray(userProfile.experience) && userProfile.experience.length) {
      const totals = userProfile.experience
        .map((role) => {
          const years = this.toNumber(role?.years || role?.durationYears);
          if (Number.isFinite(years) && years > 0) {
            return years;
          }
          return this.parseDurationString(role?.duration);
        })
        .filter((value) => Number.isFinite(value) && value > 0);

      if (totals.length) {
        const totalYears = totals.reduce((sum, value) => sum + value, 0);
        if (totalYears > 0) {
          return parseFloat(totalYears.toFixed(2));
        }
      }
    }

    return 0;
  }

  static extractNumericFields(source, keys = []) {
    if (!source) return NaN;
    for (const key of keys) {
      if (source[key] == null) continue;
      const value = this.toNumber(source[key]);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }
    return NaN;
  }

  static derivePositionDuration(position = {}) {
    if (!position) return NaN;

    const directYears = this.toNumber(
      position.durationYears ||
      position.durationInYears ||
      position.totalYears ||
      position.experienceYears
    );

    if (Number.isFinite(directYears) && directYears > 0) {
      return directYears;
    }

    if (position.startDate || position.endDate || position.start_date || position.end_date) {
      const start = this.parseDate(position.startDate || position.start_date);
      const endRaw = position.endDate || position.end_date;
      const end = this.parseDate(endRaw, true);

      if (start && end && end >= start) {
        const millisecondsPerYear = 1000 * 60 * 60 * 24 * 365.25;
        const years = (end - start) / millisecondsPerYear;
        if (years > 0) {
          return parseFloat(years.toFixed(2));
        }
      }
    }

    const durationFromText = this.parseDurationString(position.duration || position.tenure);
    if (Number.isFinite(durationFromText) && durationFromText > 0) {
      return durationFromText;
    }

    return NaN;
  }

  static parseDurationString(rawDuration) {
    if (!rawDuration || typeof rawDuration !== 'string') return NaN;
    const normalized = rawDuration.toLowerCase();

    let totalMonths = 0;

    const yearMatch = normalized.match(/(\d+(?:\.\d+)?)\s*(?:years?|yrs?|y)/);
    if (yearMatch) {
      totalMonths += parseFloat(yearMatch[1]) * 12;
    }

    const monthMatch = normalized.match(/(\d+(?:\.\d+)?)\s*(?:months?|mos?|m)/);
    if (monthMatch) {
      totalMonths += parseFloat(monthMatch[1]);
    }

    if (totalMonths > 0) {
      return parseFloat((totalMonths / 12).toFixed(2));
    }

    const numberOnly = this.toNumber(normalized);
    if (Number.isFinite(numberOnly) && numberOnly > 0) {
      return numberOnly;
    }

    return NaN;
  }

  static parseDate(value, allowPresent = false) {
    if (!value) return null;
    if (allowPresent && typeof value === 'string' && value.toLowerCase().includes('present')) {
      return new Date();
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  static parseNumericRange(condition) {
    if (!condition || typeof condition !== 'string') return null;
    const normalized = condition.toLowerCase();
    const cleaned = normalized.replace(/[,]/g, '');

    const rangeMatch = cleaned.match(/(\d+(?:\.\d+)?)\s*(?:-|\bto\b)\s*(\d+(?:\.\d+)?)/);
    if (rangeMatch) {
      const min = parseFloat(rangeMatch[1]);
      const max = parseFloat(rangeMatch[2]);
      if (Number.isFinite(min) && Number.isFinite(max)) {
        return { min, max, minInclusive: true, maxInclusive: true };
      }
    }

    const betweenMatch = cleaned.match(/between\s+(\d+(?:\.\d+)?)\s*(?:and|-)\s*(\d+(?:\.\d+)?)/);
    if (betweenMatch) {
      const min = parseFloat(betweenMatch[1]);
      const max = parseFloat(betweenMatch[2]);
      if (Number.isFinite(min) && Number.isFinite(max)) {
        return { min, max, minInclusive: true, maxInclusive: true };
      }
    }

    const minMatch = cleaned.match(/(?:>=|at least|minimum(?: of)?|not less than)\s*(\d+(?:\.\d+)?)/);
    if (minMatch) {
      const min = parseFloat(minMatch[1]);
      if (Number.isFinite(min)) {
        return { min, minInclusive: true };
      }
    }

    const greaterMatch = cleaned.match(/(?:>|more than|over)\s*(\d+(?:\.\d+)?)/);
    if (greaterMatch) {
      const min = parseFloat(greaterMatch[1]);
      if (Number.isFinite(min)) {
        return { min, minInclusive: false };
      }
    }

    const maxMatch = cleaned.match(/(?:<=|at most|maximum(?: of)?|no more than)\s*(\d+(?:\.\d+)?)/);
    if (maxMatch) {
      const max = parseFloat(maxMatch[1]);
      if (Number.isFinite(max)) {
        return { max, maxInclusive: true };
      }
    }

    const lessMatch = cleaned.match(/(?:<|less than|under)\s*(\d+(?:\.\d+)?)/);
    if (lessMatch) {
      const max = parseFloat(lessMatch[1]);
      if (Number.isFinite(max)) {
        return { max, maxInclusive: false };
      }
    }

    const exactMatch = cleaned.match(/(?:exactly|=)\s*(\d+(?:\.\d+)?)/);
    if (exactMatch) {
      const value = parseFloat(exactMatch[1]);
      if (Number.isFinite(value)) {
        return { min: value, max: value, minInclusive: true, maxInclusive: true };
      }
    }

    const standaloneNumber = cleaned.match(/(\d+(?:\.\d+)?)/);
    if (standaloneNumber) {
      const value = parseFloat(standaloneNumber[1]);
      if (Number.isFinite(value)) {
        return { min: value, minInclusive: true };
      }
    }

    return null;
  }

  static isWithinRange(value, range) {
    if (!Number.isFinite(value) || !range) return false;
    if (Number.isFinite(range.min)) {
      if (range.minInclusive === false) {
        if (value <= range.min) return false;
      } else if (value < range.min) {
        return false;
      }
    }
    if (Number.isFinite(range.max)) {
      if (range.maxInclusive === false) {
        if (value >= range.max) return false;
      } else if (value > range.max) {
        return false;
      }
    }
    return true;
  }

  static toNumber(value) {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : NaN;
    }
    if (typeof value === 'string') {
      const sanitized = value
        .replace(/[^0-9.,-]/g, '')
        .replace(/,(?=\d{3}(?:\D|$))/g, '')
        .replace(/,/g, '.');

      const parsed = parseFloat(sanitized);
      return Number.isFinite(parsed) ? parsed : NaN;
    }
    return NaN;
  }
}

export default ScoringEngine;
